// OrdersPages.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";
import ComputeBillProfitButton from "../components/ComputeBillProfitButton";

/** ---------- Types (สอดคล้องกับ /orders/summary) ---------- */
type Row = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null; // เช่น "16/10/2568"
  recTime: string | null;   // เช่น "16:01:22"
  totalItems: number;
  orderTotal: number;       // ราคารวมก่อนส่วนลด
  packageFee: number;
  shippingFee: number;
  discountAmount: number | null; // จำนวนเงินลด
  afterDiscount: number | null;  // ยอดหลังหักส่วนลด (ถ้า DB บันทึก)
  orderCreated: string;     // datetime
};

type OrdersListResponse = {
  items?: Row[];
  page?: number;
  limit?: number;
  total?: number;
  error?: string;
};

type OrderItem = {
  productCode: string;
  productName: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
};

type Order = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null;
  recTime: string | null;
  totalItems: number;
  orderTotal: number;
  packageFee: number;
  shippingFee: number;
  discountAmount: number;
  afterDiscount: number; // ถ้าไม่มีให้คำนวณเอง
  createdAt: string;
  items: OrderItem[];
};

/** ชนิดวิธีชำระเงิน (ให้สอดคล้องหน้าแสกน) */
type PaymentMethod = "เงินสด" | "สแกน" | "Alipay" | "WeChat" | "บัตรเดบิต";
const PAYMENT_OPTIONS: PaymentMethod[] = ["เงินสด", "สแกน", "Alipay", "WeChat", "บัตรเดบิต"];
const DEFAULT_VISIBLE_ORDERS = 100;
const PAGED_VISIBLE_ORDERS = 200;

/** ---------- Helpers ---------- */
const thb = (n: number) =>
  n.toLocaleString("th-TH", { style: "currency", currency: "THB" });

function rowsToOrders(rows: Row[]): Order[] {
  return rows
    .map((r) => {
      const discount = r.discountAmount ?? 0;
      const after =
        r.afterDiscount != null
          ? r.afterDiscount
          : Math.max(0, (r.orderTotal ?? 0) - discount);

      return {
        billNo: r.billNo,
        paymentMethod: r.paymentMethod ?? null,
        saleChannel: r.saleChannel ?? null,
        recDateBE: r.recDateBE ?? null,
        recTime: r.recTime ?? null,
        totalItems: r.totalItems ?? 0,
        orderTotal: r.orderTotal ?? 0,
        packageFee: r.packageFee ?? 0,
        shippingFee: r.shippingFee ?? 0,
        discountAmount: discount,
        afterDiscount: after,
        createdAt: r.orderCreated,
        items: [],
      } as Order;
    })
    .sort((a, b) => {
    const an = parseInt(a.billNo, 10);
    const bn = parseInt(b.billNo, 10);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) {
      // เลขบิลเป็นตัวเลขล้วน → เรียงจากมากไปน้อย
      return bn - an;
    }
    // ไม่ใช่ตัวเลขล้วน → เรียงสตริงแบบย้อนกลับ
    return b.billNo.localeCompare(a.billNo, undefined, { numeric: true, sensitivity: "base" });
  });
}

type OrderDetailApiItem = {
  id: number;
  code: string;
  name: string;
  qty: number;
  unitPrice: number;
};

type OrderDetailApiResponse = {
  order?: {
    billNo: string;
    paymentMethod: string | null;
    saleChannel?: string | null;
    recDateBE: string | null;
    recTime: string | null;
    packageFee?: number;
    shippingFee?: number;
    discountAmount: number;
    createdAt: string;
    items: OrderDetailApiItem[];
  };
  error?: string;
};

// ฟังก์ชันช่วย: แปลง dd/mm/yyyy (พ.ศ.) หรือ yyyy-mm-dd (พ.ศ.) เป็น Date (ค.ศ.)
function beDMYToDate(dmy: string, time?: string): Date | null {
  const hhmmss = (time && /^\d{2}:\d{2}(:\d{2})?$/.test(time)) ? time : "00:00:00";

  const slash = dmy.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const dd = Number(slash[1]);
    const mm = Number(slash[2]);
    const yyyyBE = Number(slash[3]);
    const yyyyAD = yyyyBE - 543;
    return new Date(`${yyyyAD}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}T${hhmmss}`);
  }

  const iso = dmy.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const yyyyBE = Number(iso[1]);
    const mm = Number(iso[2]);
    const dd = Number(iso[3]);
    const yyyyAD = yyyyBE - 543;
    return new Date(`${yyyyAD}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}T${hhmmss}`);
  }

  return null;
}

// ฟอร์แมตไทยทางการ: "19 ตุลาคม 2568 เวลา 00:11 น."
function formatThaiLong(dt: Date): string {
  return new Intl.DateTimeFormat("th-TH", {
    dateStyle: "long",
    timeStyle: "short",
    hour12: false,
  }).format(dt) + " น.";
}

// ฟอร์แมตไทยกระชับ: "19 ต.ค. 2568 00:11 น."
function formatThaiShort(dt: Date): string {
  const d = new Intl.DateTimeFormat("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(dt);
  return d + " น.";
}

// รวมทุกกรณี -> คืน string ที่พร้อมแสดง
function formatOrderDateTime(opts: {
  createdAt?: string;     // ใช้ตัวนี้ก่อนถ้ามี
  recDateBE?: string | null;
  recTime?: string | null;
  variant?: "long" | "short";
}): string {
  const { createdAt, recDateBE, recTime, variant = "short" } = opts;

  let dt: Date | null = null;

  // 1) ใช้วันที่บิลก่อน
  if (recDateBE) {
    dt = beDMYToDate(recDateBE, recTime ?? undefined);
  }

  // 2) ถ้า parse วันที่บิลไม่ได้ ค่อย fallback ไป createdAt
  if (!dt && createdAt) {
    const d = new Date(createdAt);
    if (!Number.isNaN(d.getTime())) dt = d;
  }

  // 3) ตีความ recDateBE เป็นสตริงวันที่สากล (fallback สุดท้าย)
  if (!dt && recDateBE) {
    const tryISO = new Date(recDateBE);
    if (!Number.isNaN(tryISO.getTime())) dt = tryISO;
  }

  if (!dt) return "-";

  return variant === "long" ? formatThaiLong(dt) : formatThaiShort(dt);
}

/** ---------- API helpers (สอดคล้องหน้าแสกน) ---------- */
// PATCH วิธีชำระเงิน
async function patchPaymentMethod(billNo: string, paymentMethod: PaymentMethod | null): Promise<void> {
  // PMN-CRITICAL(/OrdersPage): backend currently returns { message } while this helper expects ok=true; keep response shape aligned before relying on edit payment.
  const resp = await fetch(apiUrl(`/orders/${encodeURIComponent(billNo)}/payment-method`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paymentMethod }), // camelCase ตรงตาม server route ที่ใช้ในโปรเจกต์
  });
  const j = await resp.json().catch(() => ({}));
  if (!resp.ok || j?.ok !== true) throw new Error(j?.error ?? resp.statusText);
}

// POST ส่วนลดบิล (ส่ง total_amount = ยอด "หลังหักส่วนลด")
async function upsertDiscount(
  billNo: string,
  discountAmount: number,
  netTotal: number // ✅ after discount
): Promise<void> {
  // PMN-CRITICAL(/OrdersPage): sales_discounts.total_amount is the net amount after discount in this page contract.
  const resp = await fetch(apiUrl(`/sales/discounts/${encodeURIComponent(billNo)}`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ discount_amount: discountAmount, total_amount: netTotal }), // ✅ ใช้ net
  });
  const j = await resp.json().catch(() => ({}));
  if (!resp.ok || j?.ok !== true) throw new Error(j?.error ?? resp.statusText);
}

/** ============================================================= */

export default function OrdersPage(): JSX.Element {
  const [raw, setRaw] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");

  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Order | null>(null);

  // state สำหรับแก้ไขบิล
  const [editing, setEditing] = useState<boolean>(false);
  const [draftPay, setDraftPay] = useState<PaymentMethod | null>(null);
  const [draftDiscount, setDraftDiscount] = useState<number>(0);
  const [saveErr, setSaveErr] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const [showAllOrders, setShowAllOrders] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalOrders, setTotalOrders] = useState<number>(0);

  const orders = useMemo(() => rowsToOrders(raw), [raw]);

  const visibleOrders = orders;

  const totalPages = useMemo(() => {
    const pageSize = showAllOrders ? PAGED_VISIBLE_ORDERS : DEFAULT_VISIBLE_ORDERS;
    return Math.max(1, Math.ceil(totalOrders / pageSize));
  }, [totalOrders, showAllOrders]);

  const load = async (opts?: { page?: number; showAll?: boolean }) => {
    setLoading(true);
    setErr("");
    try {
      const nextShowAll = opts?.showAll ?? showAllOrders;
      const nextPage = opts?.page ?? currentPage;
      const limit = nextShowAll ? PAGED_VISIBLE_ORDERS : DEFAULT_VISIBLE_ORDERS;
      const url = new URL(apiUrl("/orders-list"));
      url.searchParams.set("page", String(nextPage));
      url.searchParams.set("limit", String(limit));
      if (query.trim()) {
        url.searchParams.set("q", query.trim());
      }

      const resp = await fetch(url.toString());
      const j = (await resp.json().catch(() => ({}))) as OrdersListResponse;
      if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
      const items: Row[] = Array.isArray(j?.items) ? j.items : [];
      setRaw(items);
      setTotalOrders(Number(j?.total ?? 0));
    } catch (e: any) {
      setErr(e?.message || "โหลดข้อมูลบิลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load({ page: currentPage, showAll: showAllOrders });
  }, [currentPage, showAllOrders, query]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  /** พิมพ์ใบเสร็จ: เปิดหน้าต่างใหม่/iframe แล้วสั่ง print */
  const printReceipt = () => {
    if (!selected) return;
    const doc = window.open("", "_blank", "width=650,height=800");
    if (!doc) return;

    const html = renderReceiptHtml(selected);
    doc.document.open();
    doc.document.write(html);
    doc.document.close();
    // รอโหลดฟอนต์/สไตล์
    setTimeout(() => doc.print(), 400);
  };

  const openView = async (o: Order): Promise<void> => {
    // PMN-CRITICAL(/OrdersPage): detail modal and profit/export flows depend on /orders-detail response shape.
    setViewLoading(true);
    setSaveErr("");
    try {
      const url = new URL(apiUrl("/orders-detail"));
      url.searchParams.set("bill_no", o.billNo);
      const resp = await fetch(url.toString());
      const j = (await resp.json().catch(() => ({}))) as OrderDetailApiResponse;
      if (!resp.ok || !j.order) {
        throw new Error(j?.error ?? resp.statusText);
      }

      setSelected({
        billNo: j.order.billNo,
        paymentMethod: j.order.paymentMethod ?? null,
        saleChannel: j.order.saleChannel ?? o.saleChannel ?? null,
        recDateBE: j.order.recDateBE ?? null,
        recTime: j.order.recTime ?? null,
        totalItems: o.totalItems,
        orderTotal: o.orderTotal,
        packageFee: Number(j.order.packageFee ?? o.packageFee ?? 0),
        shippingFee: Number(j.order.shippingFee ?? o.shippingFee ?? 0),
        discountAmount: Number(j.order.discountAmount ?? o.discountAmount ?? 0),
        afterDiscount: o.afterDiscount,
        createdAt: j.order.createdAt ?? o.createdAt,
        items: Array.isArray(j.order.items)
          ? j.order.items.map((it) => ({
              productCode: it.code ?? "-",
              productName: it.name ?? "-",
              qty: Number(it.qty ?? 0),
              unitPrice: Number(it.unitPrice ?? 0),
              lineTotal: Number(it.qty ?? 0) * Number(it.unitPrice ?? 0),
            }))
          : [],
      });
    } catch (e: any) {
      setErr(e?.message || "โหลดรายละเอียดบิลไม่สำเร็จ");
    } finally {
      setViewLoading(false);
    }
  };

  // เปิดโหมดแก้ไขในโมดัล
  const openEdit = (o: Order) => {
    setSelected(o);
    setEditing(true);
    setDraftPay((o.paymentMethod as PaymentMethod | null) ?? null);
    setDraftDiscount(o.discountAmount ?? 0);
    setSaveErr("");
  };

  // บันทึกการแก้ไข
  const saveEdits = async () => {
  // PMN-CRITICAL(/OrdersPage): edits only payment/discount metadata; no stock/order_items mutation should happen here.
  if (!selected) return;
  setSaving(true);
  setSaveErr("");

  const billNo = selected.billNo;
  const originalPay = selected.paymentMethod;
  const originalDiscount = selected.discountAmount ?? 0;

  const nextPay = draftPay ?? null;
  const nextDiscount = Math.max(0, Math.floor(draftDiscount || 0));
  const nextNet = Math.max(0, selected.orderTotal - nextDiscount); // ✅ ยอดหลังหักส่วนลด

  if (nextDiscount > selected.orderTotal) {
    setSaveErr("ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้");
    setSaving(false);
    return;
  }

  // optimistic update
  const rollback = { pay: originalPay, discount: originalDiscount };
  setSelected({ ...selected, paymentMethod: nextPay, discountAmount: nextDiscount, afterDiscount: nextNet });
  setRaw((prev) =>
    prev.map((r) =>
      r.billNo === billNo
        ? { ...r, paymentMethod: nextPay, discountAmount: nextDiscount, afterDiscount: nextNet }
        : r
    )
  );

  try {
    if (originalPay !== nextPay) {
      await patchPaymentMethod(billNo, nextPay);
    }
    if (originalDiscount !== nextDiscount) {
      await upsertDiscount(billNo, nextDiscount, nextNet); // ✅ ส่ง net ไปที่ total_amount
    }

    await load();
    setEditing(false);
  } catch (e: any) {
    // rollback ...
  } finally {
    setSaving(false);
  }
  };

  function csvEscape(v: unknown): string {
    const s = String(v ?? "");
    // ครอบด้วย " และ escape " เป็น ""
    return `"${s.replaceAll('"', '""')}"`;
  }
  
  function downloadCsv(filename: string, csvText: string): void {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
  
  function exportOrderCsv(o: Order): void {
    // 1) header ของไฟล์
    const headers = [
      "bill_no",
      "created_at",
      "rec_date_be",
      "rec_time",
      "payment_method",
      "order_total",
      "discount_amount",
      "after_discount",
      "product_code",
      "product_name",
      "qty",
      "unit_price",
      "line_total",
    ];
  
    // 2) แถว: 1 item = 1 row (ทำให้เปิดใน Excel ง่าย)
    const rows = o.items.length
      ? o.items.map((it) => [
          o.billNo,
          o.createdAt,
          o.recDateBE ?? "",
          o.recTime ?? "",
          o.paymentMethod ?? "",
          o.orderTotal,
          o.discountAmount,
          o.afterDiscount,
          it.productCode,
          it.productName,
          it.qty,
          it.unitPrice,
          it.lineTotal,
        ])
      : [
          [
            o.billNo,
            o.createdAt,
            o.recDateBE ?? "",
            o.recTime ?? "",
            o.paymentMethod ?? "",
            o.orderTotal,
            o.discountAmount,
            o.afterDiscount,
            "",
            "",
            "",
            "",
            "",
          ],
        ];
  
    const csv = [headers, ...rows]
      .map((r) => r.map(csvEscape).join(","))
      .join("\n");
  
    downloadCsv(`bill_${o.billNo}.csv`, csv);
  }

  type PriceRow = { code: string; retailPrice: number; mallPrice: number };

  async function fetchPricesByCodes(codes: string[]): Promise<PriceRow[]> {
    const resp = await fetch(apiUrl("/products/prices-by-codes"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ codes }),
    });
    const j = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(j?.error ?? resp.statusText);
    return Array.isArray(j?.items) ? j.items : [];
  }

  async function exportBillProductPricesCsv(o: Order): Promise<void> {
    const codes = o.items.map((it) => it.productCode).filter(Boolean);
    const prices = await fetchPricesByCodes(codes);
  
    const priceMap = new Map<string, { retailPrice: number; mallPrice: number }>();
    for (const p of prices) priceMap.set(p.code, { retailPrice: Number(p.retailPrice ?? 0), mallPrice: Number(p.mallPrice ?? 0) });
  
    const headers = [
      "bill_no",
      "product_code",
      "product_name",
      "qty",
      "retail_price",
      "mall_price",
    ];
  
    const rows = o.items.map((it) => {
      const p = priceMap.get(it.productCode);
      return [
        o.billNo,
        it.productCode,
        it.productName,
        it.qty,
        p?.retailPrice ?? "",
        p?.mallPrice ?? "",
      ];
    });
  
    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    downloadCsv(`bill_${o.billNo}_product_prices.csv`, csv);
  }


  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <h4 className="mb-3 text-primary text-center">รายการบิล</h4>

                {/* แถบเครื่องมือ */}
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                  <form
                    className="input-group"
                    style={{ maxWidth: 420 }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      setCurrentPage(1);
                      setQuery(queryInput.trim());
                    }}
                  >
                    <span className="input-group-text">ค้นหา</span>
                    <input
                      className="form-control"
                      placeholder="เลขบิล เช่น 13"
                      value={queryInput}
                      onChange={(e) => setQueryInput(e.target.value)}
                    />
                    <button type="submit" className="btn btn-outline-primary">
                      ค้นหา
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setQueryInput("");
                        setCurrentPage(1);
                        setQuery("");
                      }}
                      disabled={!queryInput && !query}
                    >
                      ล้าง
                    </button>
                  </form>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => void load({ page: currentPage, showAll: showAllOrders })}
                      disabled={loading}
                      title="รีเฟรชข้อมูล"
                    >
                      {loading ? "กำลังโหลด…" : "รีเฟรช"}
                    </button>
                    <a href="/" className="btn btn-link text-decoration-none">
                      ← กลับหน้าเมนู
                    </a>
                  </div>
                </div>

                {err && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    {err}
                  </div>
                )}

                {/* ตารางบิลสรุป */}
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 120 }}>เลขบิล</th>
                        <th style={{ width: 160 }}>วันที่</th>
                        <th style={{ width: 120 }}>วิธีชำระ</th>
                        <th className="text-end" style={{ width: 120 }}>
                          จำนวนสินค้า
                        </th>
                        <th className="text-end" style={{ width: 160 }}>
                          ยอดหลังหักส่วนลด
                        </th>
                        <th style={{ width: 160 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleOrders.map((o) => (
                        <tr key={o.billNo}>
                          <td className="fw-semibold text-center">#{o.billNo}</td>
                          <td>
                            {formatOrderDateTime({
                              createdAt: o.createdAt,
                              recDateBE: o.recDateBE,
                              recTime: o.recTime,
                              variant: "short", // ตารางใช้แบบกระชับ
                            })}
                          </td>
                          <td className="text-center">{o.paymentMethod ?? "-"}</td>
                          <td className="text-center">{o.totalItems}</td>
                          <td className="text-end">{thb(o.afterDiscount)}</td>
                          <td className="text-end d-flex gap-2 justify-content-end">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => openEdit(o)}
                              title="แก้ไขวิธีชำระ/ส่วนลด"
                            >
                              แก้ไข
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => void openView(o)}
                              title="ดูบิล"
                              disabled={viewLoading}
                            >
                              {viewLoading && selected?.billNo === o.billNo ? "กำลังโหลด..." : "ดูบิล"}
                            </button>
                            {/* ✅ ปุ่มคำนวณกำไร */}
                            <ComputeBillProfitButton billNo={o.billNo} />
                          </td>
                        </tr>
                      ))}
                      {!loading && visibleOrders.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            ไม่พบบิล
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {!loading && totalOrders > DEFAULT_VISIBLE_ORDERS && (
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mt-3">
                    <div className="small text-muted">
                      {showAllOrders
                        ? `หน้า ${currentPage.toLocaleString()} / ${totalPages.toLocaleString()} • กำลังแสดง ${visibleOrders.length.toLocaleString()} จาก ${totalOrders.toLocaleString()} บิล`
                        : `กำลังแสดง ${visibleOrders.length.toLocaleString()} จาก ${totalOrders.toLocaleString()} บิล`}
                    </div>
                    <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
                      {totalPages > 1 && (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            disabled={currentPage <= 1}
                          >
                            ก่อนหน้า
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={currentPage >= totalPages}
                          >
                            ถัดไป
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => {
                          setShowAllOrders((prev) => !prev);
                          setCurrentPage(1);
                        }}
                      >
                        {showAllOrders ? "แสดงเฉพาะ 100 บิลแรก" : "ดูทั้งหมด"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * สไตล์การ์ดและปุ่มยึดแนวเดียวกับหน้า ProductsAddPage
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Modal ใบเสร็จ (ดูรายละเอียด) ---------- */}
      {selected && !editing && (
        <div
          className="modal fade show"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header">
                <h5 className="modal-title">ใบเสร็จ #{selected.billNo}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setSelected(null)}
                />
              </div>

              <div className="modal-body">
                {/* Header */}
                <div className="d-flex justify-content-between flex-wrap">
                  <div>
                    <div className="fw-bold">บิลเลขที่: #{selected.billNo}</div>
                    <div className="text-muted">
                      วันที่: {formatOrderDateTime({
                        createdAt: selected.createdAt,
                        recDateBE: selected.recDateBE,
                        recTime: selected.recTime,
                        variant: "long",
                      })}
                    </div>
                    <div className="text-muted">
                      วิธีชำระ: {selected.paymentMethod ?? "-"}
                    </div>
                  </div>
                  <div className="text-end">
                    <div>ยอดก่อนส่วนลด: {thb(selected.orderTotal)}</div>
                    <div>ส่วนลด: {thb(selected.discountAmount)}</div>
                    <div className="fs-5 fw-bold">
                      ยอดสุทธิ: {thb(selected.afterDiscount)}
                    </div>
                  </div>
                </div>

                <hr />

                {/* Items */}
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 120 }}>รหัส</th>
                        <th>สินค้า</th>
                        <th className="text-end" style={{ width: 80 }}>
                          จำนวน
                        </th>
                        <th className="text-end" style={{ width: 120 }}>
                          ราคา/หน่วย
                        </th>
                        <th className="text-end" style={{ width: 140 }}>
                          รวม
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.items.map((it, idx) => (
                        <tr key={idx}>
                          <td className="text-nowrap text-center">{it.productCode}</td>
                          <td>{it.productName}</td>
                          <td className="text-end">{it.qty}</td>
                          <td className="text-end">{thb(it.unitPrice)}</td>
                          <td className="text-end">{thb(it.lineTotal)}</td>
                        </tr>
                      ))}
                      {selected.items.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            ไม่มีรายการสินค้า
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-footer d-flex justify-content-between">
                <div className="text-muted">
                  สร้างเมื่อ: {new Date(selected.createdAt).toLocaleString("th-TH")}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSelected(null)}
                  >
                    ปิด
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => openEdit(selected)}
                    title="แก้ไขวิธีชำระ/ส่วนลด"
                  >
                    แก้ไข
                  </button>
                  <button className="btn btn-primary" onClick={printReceipt}>
                    พิมพ์ใบเสร็จ
                  </button>
                  <button className="btn btn-outline-success" onClick={() => exportOrderCsv(selected)}>
                    Export CSV
                  </button>
                  <button
                    className="btn btn-outline-success"
                    disabled={saving || !selected}
                    onClick={() => selected && exportBillProductPricesCsv(selected).catch((e) => setSaveErr(String(e?.message || "export_failed")))}
                  >
                    Export ราคาปลีก/ห้าง (CSV)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Modal แก้ไขบิล (วิธีชำระ + ส่วนลด) ---------- */}
      {selected && editing && (
        <div
          className="modal fade show"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setEditing(false); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header">
                <h5 className="modal-title">แก้ไขบิล #{selected.billNo}</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setEditing(false)}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label">วิธีชำระเงิน</label>
                    <div className="d-grid gap-2">
                      {PAYMENT_OPTIONS.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          className={`btn ${draftPay === opt ? "btn-primary" : "btn-outline-primary"}`}
                          onClick={() => setDraftPay(opt)}
                          aria-pressed={draftPay === opt}
                          aria-label={`เลือกวิธีชำระ ${opt}`}
                        >
                          {opt}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`btn ${draftPay === null ? "btn-warning" : "btn-outline-warning"}`}
                        onClick={() => setDraftPay(null)}
                        aria-label="ล้างวิธีชำระเงิน"
                      >
                        ล้างค่า
                      </button>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">ส่วนลดบิล (บาท)</label>
                    <div className="input-group">
                      <span className="input-group-text">฿</span>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        step={1}
                        value={Number.isFinite(draftDiscount) ? draftDiscount : 0}
                        onChange={(e) => {
                          const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                          setDraftDiscount(v);
                          if (v > selected.orderTotal) setSaveErr("ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้");
                          else setSaveErr("");
                        }}
                      />
                    </div>
                    <div className="form-text">
                      ยอดก่อนส่วนลด: <b>{thb(selected.orderTotal)}</b> • หลังลดแล้ว:{" "}
                      <b>{thb(Math.max(0, selected.orderTotal - (draftDiscount || 0)))}</b>
                    </div>
                  </div>
                </div>

                {saveErr && (
                  <div className="alert alert-warning rounded-3 mt-3" role="alert">
                    {saveErr}
                  </div>
                )}
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <div className="text-muted">
                  สร้างเมื่อ: {new Date(selected.createdAt).toLocaleString("th-TH")}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setEditing(false)}
                    disabled={saving}
                  >
                    ยกเลิก
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => void saveEdits()}
                    disabled={saving || !!saveErr}
                    title={saveErr ? "แก้ข้อผิดพลาดก่อน" : "บันทึกการเปลี่ยนแปลง"}
                  >
                    {saving ? "กำลังบันทึก…" : "บันทึก"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** ---------- HTML สำหรับพิมพ์ใบเสร็จ (เรียบง่าย พิมพ์สวย) ---------- */
function renderReceiptHtml(o: Order): string {
  const rows = o.items
    .map(
      (it) => `
      <tr>
        <td>${escapeHtml(it.productCode)}</td>
        <td>${escapeHtml(it.productName)}</td>
        <td class="t-right">${it.qty}</td>
        <td class="t-right">${thb(it.unitPrice)}</td>
        <td class="t-right">${thb(it.lineTotal)}</td>
      </tr>`
    )
    .join("");

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Receipt #${o.billNo}</title>
<style>
  body { font-family: system-ui, -apple-system, "Segoe UI", Roboto, "TH Sarabun New", sans-serif; padding: 16px; }
  .header { display:flex; justify-content:space-between; gap:12px; }
  .muted { color:#666; font-size:12px; }
  table { width:100%; border-collapse: collapse; margin-top: 12px; }
  th, td { padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 14px; }
  th { background:#f7f7f7; text-align:left; }
  .t-right { text-align:right; }
  .summary { margin-top: 14px; }
  .summary .row { display:flex; justify-content:flex-end; gap: 24px; }
  .summary .label { color:#444; }
  .summary .value { min-width: 160px; text-align: right; }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div><strong>ใบเสร็จรับเงิน</strong></div>
      <div class="muted">บิลเลขที่: #${o.billNo}</div>
      <div class="muted">วันที่: ${o.recDateBE ?? "-"} ${o.recTime ?? ""}</div>
      <div class="muted">วิธีชำระ: ${escapeHtml(o.paymentMethod ?? "-")}</div>
    </div>
    <div style="text-align:right">
      <div class="muted">สร้างเมื่อ</div>
      <div>${new Date(o.createdAt).toLocaleString("th-TH")}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:110px">รหัส</th>
        <th>รายการสินค้า</th>
        <th class="t-right" style="width:70px">จำนวน</th>
        <th class="t-right" style="width:120px">ราคา/หน่วย</th>
        <th class="t-right" style="width:130px">รวม</th>
      </tr>
    </thead>
    <tbody>
      ${rows || '<tr><td colspan="5" class="muted" style="text-align:center">ไม่มีรายการสินค้า</td></tr>'}
    </tbody>
  </table>

  <div class="summary">
    <div class="row"><div class="label">ยอดก่อนส่วนลด</div><div class="value">${thb(o.orderTotal)}</div></div>
    <div class="row"><div class="label">ส่วนลด</div><div class="value">${thb(o.discountAmount)}</div></div>
    <div class="row"><div class="label"><strong>ยอดสุทธิ</strong></div><div class="value"><strong>${thb(o.afterDiscount)}</strong></div></div>
  </div>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
